import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Nav, NavInner, NavLogo, NavLinks, NavLink, NavMenuButton, NavThemeToggle,
  MobileMenuOverlay, MobileMenuLink,
} from '../styles/MFRealStateStyles';
import { FiMenu, FiX, FiSun, FiMoon } from 'react-icons/fi';
import { MF_BASE } from '../basePath';
import { openContactModal } from './ContactModal';
import useDarkMode from '../hooks/useDarkMode';

// Regra do projeto: light mode → mf-logo-light.png · dark mode → mf-logo-dark.png
// Nunca usar mf-logo-white.png (tem caixinha branca embutida).
const LOGO_LIGHT_MODE = '/mf-real-state/logos/mf-logo-light.png';
const LOGO_DARK_MODE = '/mf-real-state/logos/mf-logo-dark.png';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggle: toggleTheme } = useDarkMode();

  const homePath = MF_BASE || '/';
  const currentPath = location.pathname.replace(/\/$/, '') || '/';
  const isHome = currentPath === homePath || currentPath === '/mf-real-state';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);
  const goTo = (path) => { closeMenu(); navigate(path); };
  const scrollToCorretores = () => {
    closeMenu();
    if (isHome) {
      document.getElementById('corretores')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(homePath);
    }
  };

  const showSolid = scrolled || !isHome;
  const hideLogo = isHome && !scrolled;

  return (
    <Nav $scrolled={showSolid}>
      <NavInner>
        <NavLogo
          href={homePath}
          $scrolled={showSolid}
          $hidden={hideLogo}
          onClick={(e) => { e.preventDefault(); navigate(homePath); }}
        >
          <img
            src={isDark ? LOGO_DARK_MODE : LOGO_LIGHT_MODE}
            alt="MF Real Estate"
            className="mf-navbar-logo"
          />
        </NavLogo>

        <NavLinks>
          <NavLink
            href={`${MF_BASE}/imoveis`}
            $scrolled={showSolid}
            onClick={(e) => { e.preventDefault(); navigate(`${MF_BASE}/imoveis`); }}
          >
            Imóveis
          </NavLink>
          <NavLink
            href={`${MF_BASE}/blog`}
            $scrolled={showSolid}
            onClick={(e) => { e.preventDefault(); navigate(`${MF_BASE}/blog`); }}
          >
            Blog
          </NavLink>
          <NavLink
            href={`${homePath}#corretores`}
            $scrolled={showSolid}
            onClick={(e) => {
              e.preventDefault();
              if (isHome) {
                document.getElementById('corretores')?.scrollIntoView({ behavior: 'smooth' });
              } else {
                navigate(homePath);
              }
            }}
          >
            Corretores
          </NavLink>
          <NavLink
            href="#contato"
            $scrolled={showSolid}
            onClick={(e) => {
              e.preventDefault();
              openContactModal();
            }}
          >
            Contato
          </NavLink>
        </NavLinks>

        <NavThemeToggle
          $scrolled={showSolid}
          onClick={toggleTheme}
          aria-label={isDark ? 'Trocar pra modo claro' : 'Trocar pra modo escuro'}
          title={isDark ? 'Modo claro' : 'Modo escuro'}
        >
          {isDark ? <FiSun /> : <FiMoon />}
        </NavThemeToggle>

        <NavMenuButton
          $scrolled={menuOpen ? false : showSolid}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={menuOpen}
          style={menuOpen ? { color: '#FFFFFF', position: 'relative', zIndex: 101 } : undefined}
        >
          {menuOpen ? <FiX /> : <FiMenu />}
        </NavMenuButton>
      </NavInner>

      {menuOpen && (
        <MobileMenuOverlay role="dialog" aria-modal="true">
          <MobileMenuLink
            href={`${MF_BASE}/imoveis`}
            onClick={(e) => { e.preventDefault(); goTo(`${MF_BASE}/imoveis`); }}
          >
            Imóveis
          </MobileMenuLink>
          <MobileMenuLink
            href={`${MF_BASE}/blog`}
            onClick={(e) => { e.preventDefault(); goTo(`${MF_BASE}/blog`); }}
          >
            Blog
          </MobileMenuLink>
          <MobileMenuLink
            href={`${homePath}#corretores`}
            onClick={(e) => { e.preventDefault(); scrollToCorretores(); }}
          >
            Corretores
          </MobileMenuLink>
          <MobileMenuLink
            href="#contato"
            onClick={(e) => { e.preventDefault(); closeMenu(); openContactModal(); }}
          >
            Contato
          </MobileMenuLink>
        </MobileMenuOverlay>
      )}
    </Nav>
  );
}
